"use client";

import type { PreviewIntentStore } from "@/components/feature/preview-intent/preview-intent-store";
import { createPreviewIntentStore } from "@/components/feature/preview-intent/preview-intent-store";
import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useRef, useSyncExternalStore } from "react";

const PreviewIntentContext = createContext<PreviewIntentStore | null>(null);

export function PreviewIntentStoreProvider({
  store,
  children,
}: {
  store: PreviewIntentStore;
  children: ReactNode;
}) {
  return <PreviewIntentContext.Provider value={store}>{children}</PreviewIntentContext.Provider>;
}

function PausePreviewsWhenTheaterOverlayFrozen() {
  const store = usePreviewIntentStore();
  const frozen = useTheaterOverlayFrozen();

  useEffect(() => {
    if (frozen) {
      store.setActiveId(null);
    }
  }, [frozen, store]);

  return null;
}

export function PreviewIntentProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<PreviewIntentStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createPreviewIntentStore();
  }
  return (
    <PreviewIntentStoreProvider store={storeRef.current}>
      <PausePreviewsWhenTheaterOverlayFrozen />
      {children}
    </PreviewIntentStoreProvider>
  );
}

export function usePreviewIntentStoreOptional(): PreviewIntentStore | null {
  return useContext(PreviewIntentContext);
}

export function usePreviewIntentStore(): PreviewIntentStore {
  const store = useContext(PreviewIntentContext);
  if (!store) {
    throw new Error("usePreviewIntentStore must be used within PreviewIntentProvider");
  }
  return store;
}

export function useIsPreviewActive(previewId: string): boolean {
  const store = usePreviewIntentStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getActiveId() === previewId,
    () => false
  );
}
